import { Chart, Plugin } from "chart.js";

interface ISubHarmonicCursorsOptions {
  enabled?: boolean;
  maxDividers?: number;
  lineColor?: string;
  lineWidth?: number;
  dashPattern?: number[];
  xOffset?: number;
}

type ChartWithCrosshair = Chart & {
  crosshair?: {
    enabled: boolean;
    x: number | null;
  };
};

const defaultOptions: Required<Omit<ISubHarmonicCursorsOptions, "enabled">> = {
  maxDividers: 5,
  lineColor: "rgb(0, 0, 255)",
  lineWidth: 1,
  dashPattern: [],
  xOffset: 0,
};

export const SubHarmonicCursorsPlugin: Plugin<"line"> = {
  id: "subHarmonicCursors",

  afterDraw(chartInstance) {
    const chart = chartInstance as ChartWithCrosshair;

    const pluginOptions = ((chart.options as any)?.plugins || {}) as {
      subHarmonicCursors?: ISubHarmonicCursorsOptions;
    };
    const opts = pluginOptions.subHarmonicCursors || {};
    if (!opts || opts.enabled !== true) {
      return;
    }

    if (!chart.crosshair || !chart.crosshair.enabled || chart.crosshair.x == null) {
      return;
    }

    const xScale = (chart.scales as any).x;
    if (!xScale || typeof xScale.getValueForPixel !== "function") {
      return;
    }

    const cross = chart.crosshair as { baseValue?: number | null } | undefined;
    const baseFromState = cross?.baseValue;

    const {
      maxDividers = defaultOptions.maxDividers,
      lineColor = defaultOptions.lineColor,
      lineWidth = defaultOptions.lineWidth,
      dashPattern = defaultOptions.dashPattern,
      xOffset = defaultOptions.xOffset,
    } = opts;

    const baseFreq =
      typeof baseFromState === "number" && Number.isFinite(baseFromState) && baseFromState > 0
        ? baseFromState
        : xScale.getValueForPixel(chart.crosshair.x);

    if (typeof baseFreq !== "number" || !Number.isFinite(baseFreq) || baseFreq <= 0) {
      return;
    }

    const min = xScale.min as number;
    const max = xScale.max as number;
    if (!Number.isFinite(min) || !Number.isFinite(max) || min >= max) {
      return;
    }

    const ctx = chart.ctx;
    const datasets = chart.data.datasets;
    const meta0 = chart.getDatasetMeta(0) as any;
    const yAxisId = meta0?.yAxisID as string | undefined;
    const yScale = yAxisId ? (chart.scales as any)[yAxisId] : null;
    if (!yScale) {
      return;
    }

    const is3D = datasets.length > 1 && xOffset !== 0;

    type IDataPoint = { x: number; y: number };

    for (let n = 2; n <= maxDividers; n++) {
      const fn = baseFreq / n;
      if (!Number.isFinite(fn) || fn <= 0) continue;

      if (!is3D) {
        if (fn < min || fn > max) continue;
        // Обычный 2D‑режим: одна вертикальная линия
        const xPix = xScale.getPixelForValue(fn);
        ctx.save();
        ctx.setLineDash(dashPattern);
        ctx.lineWidth = lineWidth;
        ctx.strokeStyle = lineColor;
        ctx.beginPath();
        ctx.moveTo(xPix, yScale.getPixelForValue(yScale.max));
        ctx.lineTo(xPix, yScale.getPixelForValue(yScale.min));
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.restore();
        continue;
      }

      const baseX = fn;

      const points: { x: number; y: number }[] = datasets.map((ds, dsIndex) => {
        const expectedX = baseX + dsIndex * xOffset;

        let closestIdx = -1;
        let minDiff = Number.POSITIVE_INFINITY;
        let minX = Infinity;
        let maxX = -Infinity;

        (ds.data as IDataPoint[]).forEach((pt, idx) => {
          if (pt && typeof pt.x === "number") {
            minX = Math.min(minX, pt.x);
            maxX = Math.max(maxX, pt.x);
            const diff = Math.abs(pt.x - expectedX);
            if (diff < minDiff) {
              minDiff = diff;
              closestIdx = idx;
            }
          }
        });

        if (closestIdx === -1 || expectedX < minX || expectedX > maxX) {
          return { x: NaN, y: NaN };
        }

        const pt = ds.data[closestIdx] as IDataPoint;
        return {
          x: xScale.getPixelForValue(pt.x),
          y: yScale.getPixelForValue(pt.y),
        };
      });

      ctx.save();
      ctx.beginPath();
      ctx.rect(
        chart.chartArea.left,
        chart.chartArea.top,
        chart.chartArea.right - chart.chartArea.left,
        chart.chartArea.bottom - chart.chartArea.top,
      );
      ctx.clip();
      ctx.setLineDash(dashPattern);
      ctx.lineWidth = lineWidth;
      ctx.strokeStyle = lineColor;
      ctx.beginPath();

      points.forEach((pt, idx) => {
        if (!Number.isFinite(pt.x) || !Number.isFinite(pt.y)) return;
        if (idx === 0) {
          ctx.moveTo(pt.x, pt.y);
        } else {
          ctx.lineTo(pt.x, pt.y);
        }
      });

      ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
    }
  },
};

export default SubHarmonicCursorsPlugin;
